import Image from "next/image";
import { Button, ModalBase, WarningTag } from "shared-components";
import { useEffect, useState, type JSX } from "react";

import { FinalPrice } from "./FinalPrice";
import { QuantityField } from "./QuantityField";
import { SelectField } from "./SelectField";

import { MdStraighten } from "react-icons/md";

import SizesGuide from "../../../../public/images/guia_de_tamanhos.png";
import { getBlankStock } from "@/api/blank/index";
import { sortProductSizes } from "shared-components/utils/sortProductSizes";
import useSWR from "swr";

interface ProductItemValues {
  blankId: string;
  color: { colorName: string; colorHex: string };
  size: string;
  quantity: number;
}
export interface Props {
  colors: {
    colorHex: string;
    colorName: string;
  }[];
  loading?: boolean;
  storefront?: boolean;
  price: number;
  sizes: string[];
  values: ProductItemValues;
  errors?: {
    [k in keyof ProductItemValues]: string;
  };
  onAddToCart: () => void;
  /** Ver: `use-product-page.ts`: `setProductItemValues` */
  onSelect: (values: ProductItemValues) => void;
}

export const ProductItemSettings = ({
  colors,
  loading,
  storefront = false,
  price,
  sizes,
  values,
  errors,
  onAddToCart,
  onSelect,
}: Props): JSX.Element => {
  const { data, mutate } = useSWR("/blank/stock", () => getBlankStock(values.blankId));

  const [showModal, setShowModal] = useState(false);
  const [stockMap, setStockMap] = useState<Record<string, number>>({});
  const [amountAvailable, setAmountAvailable] = useState(0);

  const availableColors = data
    ? colors.filter(({ colorName }) => sizes.some((size) => (stockMap[`${colorName}_${size}`] ?? 0) > 0))
    : colors;
  const availableSizes = data
    ? sizes.filter((size) => (stockMap[`${values.color.colorName}_${size}`] ?? 0) > 0)
    : sizes;

  const getWarningText = (amount: number) => {
    if (amount === 0) {
      return "Tamanho indisponível";
    }
    return amount > 1 ? `Restam ${amount} peças` : `Resta ${amount} peça`;
  };

  useEffect(() => {
    mutate();
  }, [values.blankId]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setStockMap(data.stock);
  }, [data]);

  useEffect(() => {
    if (!stockMap) {
      return;
    }
    // Monta a chave para buscar no mapa de estoque
    const stockKey = `${values.color.colorName}_${values.size}`;
    setAmountAvailable(stockMap[stockKey] || 0);
  }, [stockMap, values.color, values.size]);

  useEffect(() => {
    // Ajusta o valor se quantity for maior que o disponível
    if (values.quantity > amountAvailable) {
      onSelect({ ...values, quantity: amountAvailable || 1 });
    }
  }, [amountAvailable, values.quantity, onSelect]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const nextColor = availableColors[0];
    const nextSize = availableSizes[0];
    const colorIsAvailable = availableColors.some((color) => color.colorHex === values.color.colorHex);
    const sizeIsAvailable = availableSizes.includes(values.size);

    if (!colorIsAvailable && nextColor) {
      onSelect({ ...values, color: nextColor });
      return;
    }

    if (!sizeIsAvailable && nextSize) {
      onSelect({ ...values, size: nextSize });
    }
  }, [data, availableColors, availableSizes, values, onSelect]);

  const handleSelectColor = (colorHex: string) => {
    const color = colors.find((color) => color.colorHex === colorHex);

    if (color) {
      onSelect({ ...values, color });
    }
  };

  return (
    <div>
      <p className="mt-2 font-bold">Detalhes da sua compra</p>

      <div className="mt-2 flex gap-2">
        <SelectField
          field="color"
          options={availableColors.map(({ colorHex, colorName }) => ({
            id: colorHex,
            value: colorHex,
            label: colorName,
          }))}
          label="Cor"
          onSelect={handleSelectColor}
          optionRenderer={(label, value) => (
            <div className="flex gap-1">
              <div
                style={{ background: value }}
                className="h-3 w-3 rounded-full outline outline-2 outline-base-input"
              />

              <b className="overflow-hidden text-ellipsis text-nowrap">{label}</b>
            </div>
          )}
          className="lg:w-[60%]"
          disabled={loading}
          value={values.color.colorHex}
          error={errors?.color}
        />

        <SelectField
          field="size"
          options={sortProductSizes(availableSizes).map((size) => ({
            id: size,
            value: size,
            label: size,
          }))}
          label="Tamanho"
          onSelect={(size) => onSelect({ ...values, size })}
          optionRenderer={(label) => <b>{label}</b>}
          disabled={loading}
          value={values.size}
          error={errors?.size}
        />
      </div>

      <div className="flex justify-end items-center gap-1 mt-2">
        <MdStraighten className="text-brand-variation-1" />
        <button
          className="text-brand-variation-1 font-bold"
          onClick={() => {
            setShowModal(true);
          }}
        >
          Guia de tamanhos
        </button>
      </div>

      {amountAvailable <= 10 && <WarningTag className="justify-center" text={getWarningText(amountAvailable)} />}

      <QuantityField
        quantity={values.quantity}
        shouldDisableIncrement={values.quantity >= amountAvailable}
        onUpdateQuantity={(quantity) => onSelect({ ...values, quantity })}
        loading={loading}
        error={errors?.quantity}
      />

      <div className="mt-2 h-[1px] w-full bg-base-input" />

      <FinalPrice price={price} quantity={values.quantity} loading={loading} />

      <Button
        className="mt-2"
        color={storefront ? "storefront-primary" : "primary"}
        onClick={onAddToCart}
        disabled={loading || amountAvailable === 0}
      >
        Adicionar ao carrinho
      </Button>

      <ModalBase
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        hasCloseButton={true}
        title="Guia de tamanhos"
        className="overflow-y-scroll"
      >
        <Image src={SizesGuide} alt="Guia de tamanhos das camisetas" className="h-auto" />
      </ModalBase>
    </div>
  );
};
